import React, { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  GraduationCap, ArrowRight, ArrowLeft, Clock, Calendar, Tag as TagIcon,
  Sparkles, ShieldCheck, BadgeCheck, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { getPostBySlug, getRelatedPosts, CATEGORIES } from "@/data/blogPosts";
import { useSeo } from "@/lib/useSeo";
import NewsletterForm from "@/components/NewsletterForm";
import LanguageHelper from "@/components/LanguageHelper";
import BlogReadingProgress from "@/components/BlogReadingProgress";
import BlogHelpfulVote from "@/components/BlogHelpfulVote";

function renderInline(node, key) {
  if (typeof node === "string") return node;
  if (node.strong) return <strong key={key}>{node.strong}</strong>;
  if (node.link) return <a key={key} href={node.link} target="_blank" rel="noopener noreferrer" className="text-violet-700 underline hover:text-violet-900">{node.text}</a>;
  return null;
}

function Block({ block }) {
  if (block.type === "h2") return <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-10 mb-3 scroll-mt-24" id={slugifyHeading(block.text)}>{block.text}</h2>;
  if (block.type === "h3") return <h3 className="font-display text-lg font-bold text-slate-900 mt-6 mb-2">{block.text}</h3>;
  if (block.type === "p") return <p className="text-slate-700 leading-relaxed mb-4 text-[17px] sm:text-base">{block.children.map((c, i) => renderInline(c, i))}</p>;
  if (block.type === "ul") return (
    <ul className="my-4 space-y-2">
      {block.items.map((it, i) => (
        <li key={i} className="flex gap-3 text-slate-700 leading-relaxed text-[17px] sm:text-base">
          <span className="text-violet-500 font-bold shrink-0">·</span>
          <span>
            {/* Items can be a string, a single inline node {strong/link}, or an array of mixed inline nodes */}
            {Array.isArray(it)
              ? it.map((c, j) => renderInline(c, j))
              : renderInline(it, i)}
          </span>
        </li>
      ))}
    </ul>
  );
  if (block.type === "callout") return (
    <aside className="my-6 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 p-5">
      <p className="font-semibold text-violet-900 text-sm">{block.title}</p>
      <p className="text-sm text-violet-900/80 mt-1.5 leading-relaxed whitespace-pre-line">{block.body}</p>
    </aside>
  );
  return null;
}

function slugifyHeading(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** Compute up to 4 "quick takeaways" from post.summary_points OR fallback to first H2/H3 headings */
function getKeyPoints(post) {
  if (Array.isArray(post.summary_points) && post.summary_points.length) return post.summary_points.slice(0, 5);
  const headings = post.blocks.filter((b) => b.type === "h2").map((b) => b.text).slice(0, 4);
  return headings;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const related = getRelatedPosts(slug, 3);
  const articleRef = useRef(null);

  const author = post?.author || "Équipe Student Hub";
  const updatedAt = post?.updatedAt || post?.date;

  useSeo({
    title: post ? `${post.title} | Blog Student Hub` : "Article introuvable",
    description: post?.description,
    canonical: post ? `https://studenthubfrance.fr/blog/${post.slug}` : undefined,
    ogImage: post?.cover,
    lang: "fr",
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: post.cover,
      keywords: post.keywords,
      datePublished: post.date,
      dateModified: updatedAt,
      inLanguage: "fr-FR",
      author: { "@type": "Organization", name: author, url: "https://studenthubfrance.fr" },
      publisher: {
        "@type": "Organization",
        name: "Student Hub",
        logo: { "@type": "ImageObject", url: "https://studenthubfrance.fr/logo.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://studenthubfrance.fr/blog/${post.slug}` },
      wordCount: post.blocks.reduce((sum, b) => sum + (b.text?.length || (b.children?.map((c) => typeof c === "string" ? c : c.text || c.strong || "").join("").length || 0)), 0),
    } : null,
  });

  // Breadcrumb JSON-LD (separate to keep main JSON-LD clean)
  React.useEffect(() => {
    if (!post) return;
    const cat = CATEGORIES[post.category];
    const ld = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://studenthubfrance.fr/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://studenthubfrance.fr/blog" },
        { "@type": "ListItem", position: 3, name: cat?.label || "Article", item: `https://studenthubfrance.fr/blog?cat=${post.category}` },
        { "@type": "ListItem", position: 4, name: post.title, item: `https://studenthubfrance.fr/blog/${post.slug}` },
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    el.dataset.seoHook = "1";
    document.head.appendChild(el);
    return () => el.remove();
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;
  const cat = CATEGORIES[post.category];
  const keyPoints = getKeyPoints(post);

  const sharePost = async () => {
    const url = `https://studenthubfrance.fr/blog/${post.slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, text: post.excerpt, url }); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papier 📋");
    } catch {
      toast.error("Impossible de partager. Copie l'URL manuellement.");
    }
  };

  return (
    <div className="min-h-screen w-full bg-white" data-testid="blog-post-page">
      <BlogReadingProgress targetRef={articleRef} />

      <header className="relative z-40 max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="post-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/30">
            <GraduationCap size={18} />
          </div>
          <span className="font-display text-lg font-bold text-slate-900 hidden sm:inline">Student Hub</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <LanguageHelper />
          <Link to="/blog" className="hidden sm:inline-flex text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors items-center gap-1">
            <ArrowLeft size={14} /> Tous les articles
          </Link>
          <Link to="/register" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors" data-testid="post-register-cta">
            Commencer <ArrowRight size={14} />
          </Link>
        </nav>
      </header>

      <article ref={articleRef} className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 mt-2" aria-label="Fil d'Ariane" data-testid="post-breadcrumb">
          <Link to="/" className="hover:text-violet-600">Accueil</Link>
          <span className="mx-1.5">/</span>
          <Link to="/blog" className="hover:text-violet-600">Blog</Link>
          <span className="mx-1.5">/</span>
          <span className="text-slate-700 font-medium truncate">{post.title}</span>
        </nav>

        <div className="mt-5 mb-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${cat.color} text-white text-[10px] font-bold uppercase tracking-wide shadow`}>
            <TagIcon size={10} /> {cat.label}
          </span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">{post.title}</h1>
        <p className="text-slate-600 mt-4 text-lg leading-relaxed">{post.excerpt}</p>

        {/* Author + meta byline */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500" data-testid="post-meta">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center text-[10px] font-bold">SH</div>
            <span className="font-medium text-slate-700">{author}</span>
          </div>
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> Publié le {new Date(post.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
          {updatedAt !== post.date && (
            <span className="inline-flex items-center gap-1">↻ Mis à jour le {new Date(updatedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
          )}
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readTime} min</span>
          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" title="Article relu par l'équipe">
            <BadgeCheck size={13} /> Article vérifié
          </span>
          <button
            onClick={sharePost}
            className="ml-auto inline-flex items-center gap-1 text-violet-600 hover:text-violet-800 font-medium"
            data-testid="post-share-btn"
            aria-label="Partager cet article"
          >
            <Share2 size={12} /> Partager
          </button>
        </div>

        {/* Cover image */}
        <div className="mt-8 rounded-3xl overflow-hidden aspect-[2/1] bg-slate-100">
          <img src={post.cover} alt={post.title} loading="eager" decoding="async" className="w-full h-full object-cover" />
        </div>

        {/* Summary / quick-takeaways box */}
        {keyPoints.length > 0 && (
          <aside
            className="mt-8 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-blue-50 to-fuchsia-50 p-6"
            data-testid="post-summary"
            aria-label="Résumé rapide de l'article"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md">
                <Sparkles size={14} />
              </div>
              <p className="font-display font-bold text-slate-900">Résumé rapide</p>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-violet-600 font-semibold">~{post.readTime} min</span>
            </div>
            <ul className="space-y-1.5">
              {keyPoints.map((pt, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug">
                  <span className="text-violet-500 font-bold shrink-0 mt-0.5">→</span>
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Article body */}
        <div className="mt-10 prose prose-slate max-w-none" data-testid="post-content">
          {post.blocks.map((block, i) => <Block key={i} block={block} />)}
        </div>

        {/* Helpful vote */}
        <BlogHelpfulVote slug={post.slug} />

        {/* Inline CTA */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-700 p-7 text-white" data-testid="post-inline-cta">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-xl font-bold">Passe à l'action avec Student Hub</h3>
              <p className="text-violet-100 text-sm mt-1.5">Toutes ces démarches sont disponibles dans l'app : tutoriels guidés, suivi de progression, communauté pour t'aider, assistant IA bienveillant.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/register" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-bold hover:bg-slate-100 transition-colors">
                  Créer mon compte gratuit <ArrowRight size={14} />
                </Link>
                <Link to="/accessibilite" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/30 transition-colors">
                  Assistant IA bienveillant
                </Link>
              </div>
              <p className="text-violet-200 text-[11px] mt-3 inline-flex items-center gap-1">
                <ShieldCheck size={11} /> Gratuit · RGPD · Données hébergées en France
              </p>
            </div>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-10">
          <NewsletterForm source={`blog-post:${post.slug}`} variant="card" />
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="mt-16" data-testid="related-posts-section">
            <h2 className="font-display text-xl font-bold text-slate-900 mb-4">À lire ensuite</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="related-posts">
              {related.map((r) => {
                const rc = CATEGORIES[r.category];
                return (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-lg transition-all">
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      <img src={r.cover} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r ${rc.color} bg-clip-text text-transparent`}>{rc.label}</span>
                      <p className="font-display font-bold text-slate-900 leading-snug mt-1 group-hover:text-violet-700 transition-colors">{r.title}</p>
                      <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Clock size={11} /> {r.readTime} min</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </article>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Student Hub · <Link to="/legal/cgu" className="hover:text-violet-600">CGU</Link> · <Link to="/legal/confidentialite" className="hover:text-violet-600">Confidentialité</Link> · <Link to="/securite" className="hover:text-violet-600">Sécurité</Link>
      </footer>
    </div>
  );
}
